// Page « Onboarding » de l'espace Statistiques (/stats/onboarding) : la Vue Onboarding du Recube Dashboard Java,
// adaptée au Bedrock (spec de Léo du 31/10). Tout est calculé par le satellite ÉCRIVAIN (routes
// /api/stats/onboarding/*) : la page ne fait qu'afficher. Paladium : son satellite, une seule ligne « Paladium ».
// NationsGlory : le groupe qui annonce la capacité « onboarding » (le lobby), forcé pour toutes les requêtes ; la page
// a son propre filtre de groupe (groupe principal du joueur) et ne montre jamais un nom de serveur.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle, ArrowDownRight, ArrowRight, ArrowUpRight, BarChart3, Clock, Copy, GraduationCap, HelpCircle, Info, Layers, Loader2,
  LogIn, RotateCcw, ShoppingCart, Timer, UserPlus, Waypoints, X, type LucideIcon,
} from 'lucide-react';
import {
  apiService,
  type OnboardingAccessResponse,
  type OnboardingAccessRow,
  type OnboardingConversionResponse,
  type OnboardingConversionRow,
  type OnboardingDistributionResponse,
  type OnboardingFunnelResponse,
  type OnboardingMetaResponse,
  type OnboardingMetric,
  type OnboardingOverviewResponse,
  type OnboardingQuery,
  type OnboardingRegistrationsResponse,
  type OnboardingTutorialResponse,
} from '@/services/api.service';
import { getGameById, supportsGameGroups } from '@/config/games';
import { useGameStore } from '@/stores/game.store';
import { useAllowedGameGroups, useGameGroupsStore } from '@/stores/gameGroups.store';
import page from '../StatsPage.module.scss';
import lb from './MoneyLeaderboardSection.module.scss';
import js from './JobsStatsSection.module.scss';
import own from './OnboardingPage.module.scss';
import { CompareColumnChart, LineCompareChart, SankeyChart, type LinePoint } from './OnboardingCharts';
import {
  EXPLAIN, FUNNEL_NODES, ONBOARDING_SATELLITE_VERSION, accessRows, activePreset, addFocus, bucketTick, bucketTitle, changeTone,
  conversionRows, focusUntil, formatChange, formatCount, formatDateFr, formatDelay, formatEuros, formatPct, formatPlaytime,
  funnelGraph, groupChoices, isIsoDate, parseFlags, parseFocus, periodPresets, previousRange, resolveWriter, serializeFlags,
  todayInParis, toggleFlag, trackingNotes, tutorialGraph, type RegistrationPeriod, type WriterTarget,
} from './onboarding';

const SUBTITLE = "Inscriptions, parcours d'accès, intégration et conversion des nouveaux joueurs";

// ==================== Requêtes ====================

interface Query<T> {
  loading: boolean;
  data: T | null;
  error: string | null;
  status: number | null;
}

/**
 * Une requête relancée quand `key` change (null = rien à lire). La dernière réponse reste affichée pendant le
 * chargement suivant ; une réponse arrivée après un changement de filtres est ignorée.
 */
function useQuery<T>(key: string | null, run: () => Promise<{ data?: T; error?: string; status?: number }>): Query<T> {
  const [state, setState] = useState<Query<T>>({ loading: key != null, data: null, error: null, status: null });
  useEffect(() => {
    if (key == null) {
      setState({ loading: false, data: null, error: null, status: null });
      return;
    }
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null, status: null }));
    void run().then((res) => {
      if (!alive) return;
      if (res.data !== undefined && res.data !== null) setState({ loading: false, data: res.data, error: null, status: null });
      else setState({ loading: false, data: null, error: res.error ?? 'Réponse vide', status: res.status ?? null });
    });
    return () => {
      alive = false;
    };
    // `run` change à chaque rendu : la clé porte tout ce dont il dépend.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return state;
}

// ==================== Page ====================

export function OnboardingPage() {
  const gameId = useGameStore((s) => s.activeGameId);
  const grouped = supportsGameGroups(gameId);
  const entry = useGameGroupsStore((s) => s.entries[gameId]);
  const groupsStatus = useGameGroupsStore((s) => s.status);
  const groupsError = useGameGroupsStore((s) => s.error);
  const fetchGroups = useGameGroupsStore((s) => s.fetch);
  const allowed = useAllowedGameGroups(gameId);
  const gameName = getGameById(gameId).name;

  // Le sélecteur global ne s'affiche pas ici (page hors usesGameGroup) : les groupes sont quand même relus par le
  // layout ; ce rappel couvre seulement un premier chargement encore jamais fait.
  useEffect(() => {
    if (grouped && groupsStatus === 'idle') void fetchGroups();
  }, [grouped, groupsStatus, fetchGroups]);

  const target = useMemo(
    () => resolveWriter(grouped, entry, groupsStatus, allowed, groupsError),
    [grouped, entry, groupsStatus, allowed, groupsError],
  );

  return target.kind === 'ready'
    ? <OnboardingBody key={`${gameId}:${target.gameGroup ?? '-'}`} gameGroup={target.gameGroup} gameName={gameName} allowed={allowed} />
    : <Frame><TargetMessage target={target} gameName={gameName} /></Frame>;
}

/** En-tête de la page (titre, sous-titre, note du suivi) et son contenu. */
function Frame({ notes, children }: { notes?: string[]; children: ReactNode }) {
  return (
    <div className={page.page}>
      <header className={page.header}>
        <div>
          <h1 className={page.title}>Onboarding</h1>
          <p className={page.subtitle}>{SUBTITLE}</p>
          {notes && notes.length > 0 && (
            <ul className={own.notes}>{notes.map((n) => <li key={n}>{n}</li>)}</ul>
          )}
        </div>
      </header>
      <div className={page.content}>{children}</div>
    </div>
  );
}

function Message({ icon: Icon = Info, children }: { icon?: LucideIcon; children: ReactNode }) {
  return (
    <div className={own.message} role="status">
      <Icon size={20} className={js.stateIcon} />
      <div className={js.stateText}>{children}</div>
    </div>
  );
}

function TargetMessage({ target, gameName }: { target: Exclude<WriterTarget, { kind: 'ready' }>; gameName: string }) {
  switch (target.kind) {
    case 'loading':
      return <div className={lb.state}><Loader2 size={20} className={own.spin} /> Chargement des groupes de jeu…</div>;
    case 'error':
      return <Message icon={AlertCircle}>Groupes de jeu de {gameName} illisibles ({target.message}) : recharge la page dans un instant.</Message>;
    case 'none':
      return (
        <Message>
          L'onboarding n'est pas encore activé sur {gameName} : aucun satellite ne l'écrit (satellite du lobby à configurer,
          réglage onboarding.writer).
        </Message>
      );
    case 'notMember':
      return (
        <Message>
          Pour voir l'onboarding de {gameName}, il faut être affecté au groupe {target.label} (Paramètres &gt; Grades &amp; accès).
          Demande l'accès à un responsable du panel.
        </Message>
      );
    case 'unavailable':
      return <Message icon={AlertCircle}>Satellite du groupe {target.label} indisponible : {target.reason}.</Message>;
  }
}

/** Lecture de meta sur le satellite écrivain, puis le tableau de bord. */
function OnboardingBody({ gameGroup, gameName, allowed }: { gameGroup: string | null; gameName: string; allowed: string[] | null }) {
  const meta = useQuery<OnboardingMetaResponse>(`meta:${gameGroup ?? '-'}`, () => apiService.getOnboardingMeta(gameGroup));

  if (meta.loading && !meta.data) {
    return <Frame><div className={lb.state}><Loader2 size={20} className={own.spin} /> Chargement…</div></Frame>;
  }
  if (!meta.data) {
    return (
      <Frame>
        {meta.status === 404
          ? <Message>Satellite de {gameName} à mettre à jour ({ONBOARDING_SATELLITE_VERSION} ou plus) pour afficher l'onboarding.</Message>
          : <Message icon={AlertCircle}>Onboarding illisible : {meta.error ?? 'réponse vide'}.</Message>}
      </Frame>
    );
  }
  if (!meta.data.writer) {
    return (
      <Frame>
        <Message>
          Ce satellite n'écrit pas l'onboarding de {gameName} (réglage onboarding.writer du config.yaml du satellite).
        </Message>
      </Frame>
    );
  }
  return (
    <Frame notes={trackingNotes(meta.data)}>
      <Dashboard meta={meta.data} gameGroup={gameGroup} allowed={allowed} />
    </Frame>
  );
}

// ==================== Tableau de bord ====================

const PERIODS: { id: RegistrationPeriod; label: string }[] = [
  { id: 'day', label: 'Jour' },
  { id: 'week', label: 'Semaine' },
  { id: 'month', label: 'Mois' },
];

function Dashboard({ meta, gameGroup, allowed }: { meta: OnboardingMetaResponse; gameGroup: string | null; allowed: string[] | null }) {
  const [params, setParams] = useSearchParams();
  const today = useMemo(() => todayInParis(), []);
  const presets = useMemo(() => periodPresets(today, meta.trackedSince), [today, meta.trackedSince]);
  const [regCompare, setRegCompare] = useState(false);
  const [distCompare, setDistCompare] = useState(false);

  // Filtres dans l'URL : un lien copié rouvre la même vue (flags compris).
  const rawStart = params.get('startDate');
  const rawEnd = params.get('endDate');
  const startDate = isIsoDate(rawStart) ? rawStart : presets[0].startDate;
  const endDate = isIsoDate(rawEnd) ? rawEnd : presets[0].endDate;
  const rangeError = startDate > endDate ? 'La date de début est après la date de fin.' : null;
  const groups = groupChoices(meta, allowed);
  const rawGroup = params.get('group');
  const group = rawGroup && groups.some((g) => g.key === rawGroup) ? rawGroup : null;
  const criteriaOrder = useMemo(() => meta.criteria.map((c) => c.key), [meta.criteria]);
  const flagStates = parseFlags(params.get('flags'), criteriaOrder);
  const flags = serializeFlags(flagStates, criteriaOrder);
  const focus = parseFocus(params.get('focus'));
  const rawPeriod = params.get('period');
  const period: RegistrationPeriod = rawPeriod === 'week' || rawPeriod === 'month' ? rawPeriod : 'day';

  const update = (changes: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(changes)) {
        if (v == null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  };

  const q: OnboardingQuery = { startDate, endDate, group, flags: flags || undefined };
  const base = rangeError ? null : `${startDate}|${endDate}|${group ?? ''}|${flags}`;
  const tableBase = rangeError ? null : `${startDate}|${endDate}|${flags}`;
  const focusParam = focus.join(',');
  const hasTuto = meta.tutorialSteps.length > 0;

  const overview = useQuery<OnboardingOverviewResponse>(base && `ov:${base}`, () => apiService.getOnboardingOverview(q, gameGroup));
  const registrations = useQuery<OnboardingRegistrationsResponse>(base && `reg:${base}|${period}|${regCompare}`,
    () => apiService.getOnboardingRegistrations(q, period, regCompare, gameGroup));
  const funnel = useQuery<OnboardingFunnelResponse>(base && `fun:${base}|${focusParam}`,
    () => apiService.getOnboardingFunnel({ ...q, focus: focusParam || undefined }, gameGroup));
  const tutorial = useQuery<OnboardingTutorialResponse>(base && hasTuto ? `tut:${base}|${focusParam}` : null,
    () => apiService.getOnboardingTutorial({ ...q, focus: focusParam || undefined }, gameGroup));
  const access = useQuery<OnboardingAccessResponse>(tableBase && `acc:${tableBase}`, () => apiService.getOnboardingGroupAccess(q, gameGroup));
  const conversion = useQuery<OnboardingConversionResponse>(tableBase && meta.store ? `conv:${tableBase}` : null,
    () => apiService.getOnboardingGroupConversion(q, gameGroup));
  const distribution = useQuery<OnboardingDistributionResponse>(base && `dist:${base}|${distCompare}`,
    () => apiService.getOnboardingDistribution(q, distCompare, gameGroup));

  const prev = previousRange({ startDate, endDate });
  const preset = activePreset({ startDate, endDate }, presets);
  const hasGroups = meta.groups.length > 0;

  return (
    <div className={own.wrap}>
      {meta.reprise === 'running' && (
        <div className={js.info}><Info size={16} /><span>Reprise de l'historique en cours : les périodes anciennes se complètent.</span></div>
      )}

      <section className={js.block} aria-label="Filtres">
        <div className={own.filterRow}>
          <span className={own.filterLabel}>Période</span>
          <input type="date" className={own.dateInput} value={startDate} max={endDate} aria-label="Date de début"
            onChange={(e) => { if (isIsoDate(e.target.value)) update({ startDate: e.target.value, endDate }); }} />
          <span className={js.muted}>au</span>
          <input type="date" className={own.dateInput} value={endDate} min={startDate} aria-label="Date de fin"
            onChange={(e) => { if (isIsoDate(e.target.value)) update({ startDate, endDate: e.target.value }); }} />
        </div>
        <div className={js.chips} role="group" aria-label="Raccourcis de période">
          {presets.map((p) => (
            <button key={p.id} type="button" className={`${js.chip} ${preset === p.id ? js.chipActive : ''}`}
              aria-pressed={preset === p.id} onClick={() => update({ startDate: p.startDate, endDate: p.endDate })}>
              {p.label}
            </button>
          ))}
        </div>
        <p className={own.periodText}>
          {rangeError ?? <>Du {formatDateFr(startDate)} au {formatDateFr(endDate)} · comparaison N-1 : du {formatDateFr(prev.startDate)} au {formatDateFr(prev.endDate)}</>}
        </p>
        {hasGroups && (
          <div className={own.filterRow}>
            <span className={own.filterLabel}>Groupe</span>
            <div className={js.chips} role="group" aria-label="Groupe principal des joueurs">
              <button type="button" className={`${js.chip} ${group == null ? js.chipActive : ''}`} aria-pressed={group == null}
                onClick={() => update({ group: null })}>Tous</button>
              {groups.map((g) => (
                <button key={g.key} type="button" className={`${js.chip} ${group === g.key ? js.chipActive : ''}`} aria-pressed={group === g.key}
                  onClick={() => update({ group: g.key })}>{g.label}</button>
              ))}
            </div>
          </div>
        )}
        {meta.criteria.length > 0 && (
          <div className={own.filterRow}>
            <span className={own.filterLabel}>Critères onboarding</span>
            <div className={js.chips} role="group" aria-label="Critères onboarding">
              {meta.criteria.map((c) => {
                const state = flagStates.get(c.key);
                const cls = state === 'must' ? own.critMust : state === 'not' ? own.critNot : '';
                const title = state === 'must' ? 'DOIT avoir (clic : NE DOIT PAS avoir)'
                  : state === 'not' ? 'NE DOIT PAS avoir (clic : neutre)' : 'Neutre (clic : DOIT avoir)';
                return (
                  <button key={c.key} type="button" className={`${js.chip} ${cls}`} title={title}
                    aria-label={`${c.label} : ${title}`}
                    onClick={() => update({ flags: toggleFlag(flags, c.key, criteriaOrder) || null })}>
                    {state === 'must' && <span className={own.critMark}>DOIT</span>}
                    {state === 'not' && <span className={own.critMark}>PAS</span>}
                    {c.label}
                  </button>
                );
              })}
              {flags && (
                <button type="button" className={own.reset} onClick={() => update({ flags: null })}>
                  <RotateCcw size={14} /> Tout neutre
                </button>
              )}
            </div>
          </div>
        )}
        <p className={js.muted}>
          Critères : un clic = DOIT avoir, deux clics = NE DOIT PAS avoir, trois clics = neutre. Ils se combinent (ET) et
          s'appliquent à toute la page.
        </p>
      </section>

      {rangeError ? <Message icon={AlertCircle}>{rangeError}</Message> : (
        <>
          <Cards meta={meta} query={overview} />
          <RegistrationsBlock query={registrations} period={period} compare={regCompare}
            onPeriod={(p) => update({ period: p === 'day' ? null : p })} onCompare={setRegCompare} />
          <FunnelBlock query={funnel} focus={focus}
            onFocus={(next) => update({ focus: next.length > 0 ? next.join(',') : null })} />
          {hasTuto && <TutorialBlock query={tutorial} focus={focus} />}
          <AccessBlock meta={meta} query={access} hasGroups={hasGroups} allowed={allowed} />
          {meta.store && <ConversionBlock meta={meta} query={conversion} hasGroups={hasGroups} allowed={allowed} />}
          <DistributionBlock query={distribution} compare={distCompare} onCompare={setDistCompare} />
        </>
      )}
    </div>
  );
}

// ==================== Blocs ====================

function Explain({ text }: { text: string }) {
  return (
    <span className={own.explain} tabIndex={0} role="note" aria-label={`Explication : ${text}`}>
      <HelpCircle size={13} /> Explication
      <span className={own.explainBox} aria-hidden="true">{text}</span>
    </span>
  );
}

function Loading({ show }: { show: boolean }) {
  return show ? <span className={own.loadingInline}><Loader2 size={13} className={own.spin} /> Mise à jour…</span> : null;
}

/** Contenu d'un bloc selon l'état de sa requête. */
function Body<T>({ query, children }: { query: Query<T>; children: (data: T) => ReactNode }) {
  if (query.data) return <>{children(query.data)}</>;
  if (query.loading) return <div className={lb.state}><Loader2 size={18} className={own.spin} /> Chargement…</div>;
  return <p className={lb.notice}>{query.error ?? 'Pas de données.'}</p>;
}

function Cards({ meta, query }: { meta: OnboardingMetaResponse; query: Query<OnboardingOverviewResponse> }) {
  return (
    <section className={js.block} aria-label="Indicateurs">
      <div className={js.blockHead}>
        <h3><UserPlus size={18} /> Indicateurs</h3>
        <div className={own.headTools}><Loading show={query.loading && !!query.data} /><Explain text={EXPLAIN.cards} /></div>
      </div>
      <Body query={query}>
        {(ov) => (
          <div className={`${lb.cards} ${own.cards5}`}>
            <Card icon={UserPlus} label="Inscriptions" metric={ov.registrations} format={formatCount} />
            <Card icon={LogIn} label="% Login serveur de jeu" metric={ov.loginInGameRate} format={formatPct} />
            <Card icon={Timer} label="% Joué 1h" metric={ov.played1hRate} format={formatPct} />
            <Card icon={Clock} label="Temps de jeu moyen" metric={ov.avgPlaytime} format={formatPlaytime} />
            <Card icon={Copy} label="% Double compte" metric={ov.doubleAccountRate} format={formatPct} higherIsWorse
              unavailable={meta.doubleAccount ? undefined : 'non configuré (empreinte des IP)'} />
          </div>
        )}
      </Body>
    </section>
  );
}

function Card({ icon: Icon, label, metric, format, higherIsWorse = false, unavailable }: {
  icon: LucideIcon;
  label: string;
  metric: OnboardingMetric;
  format: (v: number | null) => string;
  higherIsWorse?: boolean;
  unavailable?: string;
}) {
  const tone = changeTone(metric.change, higherIsWorse);
  const Arrow = metric.change == null || metric.change === 0 ? ArrowRight : metric.change > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <div className={lb.card}>
      <div className={lb.cardIcon}><Icon size={22} strokeWidth={2} /></div>
      <div className={lb.cardText}>
        <span className={lb.cardValue}>{unavailable ? '-' : format(metric.value)}</span>
        <span className={lb.cardLabel}>{label}</span>
        {unavailable ? <span className={lb.cardSub}>{unavailable}</span> : (
          <span className={`${own.change} ${own[tone]}`} title="Variation face à la même période un an avant">
            {tone !== 'none' && <Arrow size={13} />}
            {formatChange(metric.change)}{tone !== 'none' && ' vs N-1'}
          </span>
        )}
      </div>
    </div>
  );
}

function RegistrationsBlock({ query, period, compare, onPeriod, onCompare }: {
  query: Query<OnboardingRegistrationsResponse>;
  period: RegistrationPeriod;
  compare: boolean;
  onPeriod: (p: RegistrationPeriod) => void;
  onCompare: (v: boolean) => void;
}) {
  return (
    <section className={js.block}>
      <div className={js.blockHead}>
        <h3><UserPlus size={18} /> Inscriptions</h3>
        <div className={own.headTools}>
          <Loading show={query.loading && !!query.data} />
          <div className={js.chips} role="group" aria-label="Regroupement">
            {PERIODS.map((p) => (
              <button key={p.id} type="button" className={`${js.chip} ${period === p.id ? js.chipActive : ''}`} aria-pressed={period === p.id}
                onClick={() => onPeriod(p.id)}>{p.label}</button>
            ))}
          </div>
          <label className={own.compare}><input type="checkbox" checked={compare} onChange={(e) => onCompare(e.target.checked)} /> Comparer</label>
          <Explain text={EXPLAIN.registrations} />
        </div>
      </div>
      <Body query={query}>
        {(r) => {
          if (r.data.length === 0) return <p className={lb.empty}>Aucun jour dans la période.</p>;
          const points: LinePoint[] = r.data.map((d) => ({ key: d.date, tick: bucketTick(d.date, period), title: bucketTitle(d.date, period), value: d.count }));
          const previous: LinePoint[] | null = compare && r.previous.length > 0
            ? r.previous.map((d) => ({ key: d.date, tick: bucketTick(d.date, period), title: bucketTitle(d.date, period), value: d.count }))
            : null;
          return (
            <LineCompareChart points={points} previous={previous} formatValue={formatCount} height={230}
              ariaLabel="Inscriptions par période" currentLabel="Période" previousLabel="Même période un an avant" />
          );
        }}
      </Body>
    </section>
  );
}

function FunnelBlock({ query, focus, onFocus }: {
  query: Query<OnboardingFunnelResponse>;
  focus: string[];
  onFocus: (next: string[]) => void;
}) {
  const graph = useMemo(() => (query.data ? funnelGraph(query.data) : null), [query.data]);
  return (
    <section className={js.block}>
      <div className={js.blockHead}>
        <h3><Waypoints size={18} /> Parcours d'accès</h3>
        <div className={own.headTools}><Loading show={query.loading && !!query.data} /><Explain text={EXPLAIN.funnel} /></div>
      </div>
      <nav className={own.crumbs} aria-label="Échantillon du parcours">
        {focus.length === 0
          ? <span className={own.crumbCurrent}>Tous les inscrits</span>
          : <button type="button" className={own.crumb} onClick={() => onFocus([])}>Tous les inscrits</button>}
        {focus.map((id, i) => (
          <span key={id} className={own.crumbs}>
            <span className={own.crumbSep} aria-hidden="true">›</span>
            {i === focus.length - 1
              ? <span className={own.crumbCurrent}>{FUNNEL_NODES[id]}</span>
              : <button type="button" className={own.crumb} onClick={() => onFocus(focusUntil(focus, i))}>{FUNNEL_NODES[id]}</button>}
          </span>
        ))}
        {focus.length > 0 && (
          <button type="button" className={own.reset} onClick={() => onFocus([])} aria-label="Revenir à tous les inscrits"><X size={14} /></button>
        )}
      </nav>
      <Body query={query}>
        {(tree) => (tree.registrations === 0 && focus.length === 0
          ? <p className={lb.empty}>Aucun inscrit sur la période avec ces critères.</p>
          : graph && (
            <>
              <SankeyChart graph={graph} formatValue={formatCount} height={tree.tuto ? 400 : 320} minWidth={tree.tuto ? 900 : 720}
                ariaLabel="Parcours d'accès des inscrits" onNodeClick={(ids) => onFocus(addFocus(focus, ids))} hideEmpty={focus.length > 0}
                labelsBelow={['lost']} />
              <p className={own.scrollHint}>Le diagramme défile de côté.</p>
              <p className={own.legendText}>
                Largeur des bandes = nombre de joueurs · % = part des inscrits de la période · clic sur un nœud = ne garder que ces joueurs
              </p>
            </>
          ))}
      </Body>
    </section>
  );
}

function TutorialBlock({ query, focus }: { query: Query<OnboardingTutorialResponse>; focus: string[] }) {
  const flow = query.data?.flow ?? null;
  const graph = useMemo(() => (flow ? tutorialGraph(flow) : null), [flow]);
  return (
    <section className={js.block}>
      <div className={js.blockHead}>
        <h3><GraduationCap size={18} /> Tutoriel d'accueil</h3>
        <div className={own.headTools}><Loading show={query.loading && !!query.data} /><Explain text={EXPLAIN.tutorial} /></div>
      </div>
      {focus.length > 0 && <p className={js.muted}>Échantillon du parcours : {focus.map((id) => FUNNEL_NODES[id]).join(' › ')}.</p>}
      <Body query={query}>
        {(t) => {
          if (!t.flow || t.flow.entered === 0) return <p className={lb.empty}>Aucun joueur arrivé au tuto sur la période.</p>;
          return (
            <>
              {graph && (
                <SankeyChart graph={graph} formatValue={formatCount} height={340} minWidth={Math.max(640, 112 * (t.flow.steps.length + 2))}
                  ariaLabel="Parcours du tutoriel d'accueil" labelsBelow={['stop']} />
              )}
              <p className={own.scrollHint}>Le diagramme défile de côté.</p>
              <div className={lb.tableWrap}>
                <table className={lb.table}>
                  <thead>
                    <tr>
                      <th>Étape</th>
                      <th className={lb.num}>Joueurs</th>
                      <th className={lb.num}>% des arrivés au tuto</th>
                      {t.delays && <th className={lb.num}>Délai médian depuis l'arrivée au tuto</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {t.data.map((s) => (
                      <tr key={s.key}>
                        <td>{s.label}</td>
                        <td className={lb.num}>{formatCount(s.count)}</td>
                        <td className={lb.num}>{formatPct(s.pctOfArrived)}</td>
                        {t.delays && <td className={lb.num}>{formatDelay(s.medianFromArrived)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!t.delays && (
                <p className={js.muted}>Délais médians masqués : les dates de passage des étapes ne sont pas encore fournies par le tuto.</p>
              )}
            </>
          );
        }}
      </Body>
    </section>
  );
}

/** Pourcentage et, dessous, une précision (délai médian, base). */
function PctCell({ pct, sub }: { pct: number | null; sub?: string | null }) {
  return (
    <span className={own.cell}>
      <span>{formatPct(pct)}</span>
      {sub && <span className={own.cellSub}>{sub}</span>}
    </span>
  );
}

function AccessBlock({ meta, query, hasGroups, allowed }: {
  meta: OnboardingMetaResponse;
  query: Query<OnboardingAccessResponse>;
  hasGroups: boolean;
  allowed: string[] | null;
}) {
  return (
    <section className={js.block}>
      <div className={js.blockHead}>
        <h3><Layers size={18} /> {hasGroups ? "Parcours d'intégration par groupe" : "Parcours d'intégration"}</h3>
        <div className={own.headTools}><Loading show={query.loading && !!query.data} /><Explain text={EXPLAIN.access} /></div>
      </div>
      <Body query={query}>
        {(resp) => {
          const { pinned, rows } = accessRows(resp, hasGroups, allowed);
          const row = (r: OnboardingAccessRow, isPinned: boolean) => (
            <tr key={r.group} className={isPinned ? own.pinned : undefined}>
              <td>{r.label}</td>
              <td className={lb.num}>{formatCount(r.players)}</td>
              <td className={lb.num}>{formatPct(r.played5minPct)}</td>
              <td className={lb.num}>{formatPct(r.played1hPct)}</td>
              {meta.integration.map((c) => {
                const cell = r.criteria[c.key];
                return (
                  <td key={c.key} className={lb.num}>
                    <PctCell pct={cell?.pct ?? null} sub={cell?.medianDelay != null ? `méd. ${formatDelay(cell.medianDelay)}` : null} />
                  </td>
                );
              })}
              <td className={lb.num}>
                <PctCell pct={r.abandon2d} sub={r.abandonBase > 0 ? `sur ${formatCount(r.abandonBase)}` : 'base vide'} />
              </td>
            </tr>
          );
          return (
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead>
                  <tr>
                    <th>{hasGroups ? 'Groupe' : 'Projet'}</th>
                    <th className={lb.num}>Joueurs</th>
                    <th className={lb.num}>5 min de jeu</th>
                    <th className={lb.num}>1 h de jeu</th>
                    {meta.integration.map((c) => <th key={c.key} className={lb.num}>{c.label}</th>)}
                    <th className={lb.num} title="Inscrits depuis au moins 7 jours, au moins 1 h de jeu : dernière connexion dans les 2 jours suivant l'inscription">
                      Abandon &lt; 2 j
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pinned && row(pinned, true)}
                  {rows.map((r) => row(r, false))}
                </tbody>
              </table>
            </div>
          );
        }}
      </Body>
    </section>
  );
}

function ConversionBlock({ meta, query, hasGroups, allowed }: {
  meta: OnboardingMetaResponse;
  query: Query<OnboardingConversionResponse>;
  hasGroups: boolean;
  allowed: string[] | null;
}) {
  return (
    <section className={js.block}>
      <div className={js.blockHead}>
        <h3><ShoppingCart size={18} /> {hasGroups ? 'Parcours de conversion par groupe' : 'Parcours de conversion'}</h3>
        <div className={own.headTools}><Loading show={query.loading && !!query.data} /><Explain text={EXPLAIN.conversion} /></div>
      </div>
      <Body query={query}>
        {(resp) => {
          const { pinned, rows } = conversionRows(resp, hasGroups, allowed);
          const row = (r: OnboardingConversionRow, isPinned: boolean) => (
            <tr key={r.group} className={isPinned ? own.pinned : undefined}>
              <td>{r.label}</td>
              <td className={lb.num}>{formatCount(r.players)}</td>
              <td className={lb.num}>
                <PctCell pct={r.store?.pct ?? null} sub={r.store?.medianDelay != null ? `méd. ${formatDelay(r.store.medianDelay)}` : null} />
              </td>
              <td className={lb.num}>
                <PctCell pct={r.rank?.pct ?? null} sub={r.rank?.medianDelay != null ? `méd. ${formatDelay(r.rank.medianDelay)}` : null} />
              </td>
              {meta.ranks.map((k) => <td key={k.key} className={lb.num}>{formatPct(r.ranks[k.key] ?? null)}</td>)}
              <td className={lb.num}>{formatEuros(r.ca)}</td>
              <td className={lb.num}><PctCellEuros avg={r.avgPerPlayer} median={r.medianPerPlayer} /></td>
              <td className={lb.num}><PctCellEuros avg={r.avgPerBuyer} median={r.medianPerBuyer} /></td>
            </tr>
          );
          return (
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead>
                  <tr>
                    <th>{hasGroups ? 'Groupe' : 'Projet'}</th>
                    <th className={lb.num}>Joueurs</th>
                    <th className={lb.num}>1er achat boutique</th>
                    <th className={lb.num}>1er grade boutique</th>
                    {meta.ranks.map((k) => <th key={k.key} className={lb.num}>{k.label}</th>)}
                    <th className={lb.num}>CA</th>
                    <th className={lb.num} title="Moyenne et médiane, non-acheteurs comptés à 0 €">Par joueur</th>
                    <th className={lb.num} title="Moyenne et médiane, acheteurs seulement">Par client</th>
                  </tr>
                </thead>
                <tbody>
                  {pinned && row(pinned, true)}
                  {rows.map((r) => row(r, false))}
                </tbody>
              </table>
            </div>
          );
        }}
      </Body>
    </section>
  );
}

function PctCellEuros({ avg, median }: { avg: number | null; median: number | null }) {
  return (
    <span className={own.cell}>
      <span>{formatEuros(avg)}</span>
      <span className={own.cellSub}>méd. {formatEuros(median)}</span>
    </span>
  );
}

function DistributionBlock({ query, compare, onCompare }: {
  query: Query<OnboardingDistributionResponse>;
  compare: boolean;
  onCompare: (v: boolean) => void;
}) {
  return (
    <section className={js.block}>
      <div className={js.blockHead}>
        <h3><BarChart3 size={18} /> Distribution du temps de jeu</h3>
        <div className={own.headTools}>
          <Loading show={query.loading && !!query.data} />
          <label className={own.compare}><input type="checkbox" checked={compare} onChange={(e) => onCompare(e.target.checked)} /> Comparer</label>
          <Explain text={EXPLAIN.distribution} />
        </div>
      </div>
      <Body query={query}>
        {(d) => {
          const total = d.data.reduce((s, b) => s + b.players, 0);
          if (total === 0 && !(compare && d.previous.some((b) => b.players > 0))) {
            return <p className={lb.empty}>Aucun inscrit sur la période avec ces critères.</p>;
          }
          return (
            <CompareColumnChart
              data={d.data.map((b, i) => ({
                key: b.bucket, label: b.bucket, title: b.max == null ? `Plus de 10 jours de jeu` : `Jusqu'à ${b.bucket}`,
                value: b.players, previous: compare ? d.previous[i]?.players ?? 0 : null,
              }))}
              showPrevious={compare && d.previous.length > 0} formatValue={formatCount} height={230}
              ariaLabel="Inscrits par tranche de temps de jeu" currentLabel="Période" previousLabel="Même période un an avant"
            />
          );
        }}
      </Body>
    </section>
  );
}
